import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon, statusMeta } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import Pager from "@/components/Pager";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["", "GENERATED", "PRINTED", "UNASSIGNED", "ACTIVE", "LOST", "DISABLED", "REVOKED"];

export default function AdminTags() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", category_id: "" });
  const [detail, setDetail] = useState(null);
  const [skip, setSkip] = useState(0);
  const LIMIT = 20;

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.status) params.set("status", filters.status);
    if (filters.category_id) params.set("category_id", filters.category_id);
    params.set("skip", skip);
    params.set("limit", LIMIT);
    const { data } = await api.get(`/admin/tags?${params}`);
    setData(data.data);
  }, [filters, skip]);

  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [filters]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/tags/${id}`);
    setDetail(data.data);
  };

  const act = async (id, action) => {
    try { await api.post(`/admin/tags/${id}/status`, { action }); toast.success(`Tag ${action}d`); load(); if (detail) openDetail(id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const regen = async (id) => {
    try { const { data } = await api.post(`/admin/tags/${id}/regenerate-code`); toast.success("New code: " + data.data.auth_code); if (detail) openDetail(id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const exportCsv = () => {
    const params = filters.category_id ? `?category_id=${filters.category_id}` : "";
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/tags${params}`, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">All Tags</h1>
          <p className="text-muted-foreground mt-1">{data?.total ?? "…"} tags total</p>
        </div>
        <button data-testid="export-tags-btn" onClick={exportCsv} className="tap px-5 py-2.5 rounded-full border border-border font-semibold text-sm flex items-center gap-2">
          <Icon name="Download" className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="Search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="tag-search" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search tag number / token" className="w-full h-11 pl-9 pr-4 rounded-full border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 h-11 rounded-full" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.category_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category_id: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 h-11 rounded-full" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-border bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-muted-foreground">
              <tr>
                {["Tag Number", "Category", "Owner", "Status", "Last Scan", ""].map((h) => (
                  <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data === null ? (
                <tr><td colSpan={6} className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></td></tr>
              ) : data.items.map((t) => {
                const m = statusMeta(t.status);
                return (
                  <tr key={t.id} className="border-t border-border hover:bg-secondary/40" data-testid={`admin-tag-row-${t.tag_number}`}>
                    <td className="px-4 py-3 font-mono font-semibold whitespace-nowrap">{t.tag_number}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.category?.name || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.owner?.name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: m.bg, color: m.color }}><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />{m.label}</span></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{t.last_scan ? new Date(t.last_scan).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right"><button data-testid={`view-tag-${t.tag_number}`} onClick={() => openDetail(t.id)} className="tap font-semibold text-status-active">View</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.items.length > 0 && <Pager skip={skip} limit={LIMIT} total={data.total} onPage={setSkip} />}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl font-mono">{detail.tag_number}</DialogTitle></DialogHeader>
              <div className="flex gap-4 items-start">
                <img src={detail.qr} alt="QR" className="w-28 h-28 border border-border rounded-lg" />
                <div className="text-sm space-y-1.5 flex-1">
                  <Row k="Status" v={statusMeta(detail.status).label} />
                  <Row k="Category" v={detail.category?.name} />
                  <Row k="Owner" v={detail.owner ? `${detail.owner.name} (${detail.owner.email})` : "Unassigned"} />
                  <Row k="Auth code" v={<span className="font-mono">{detail.auth_code}</span>} />
                  <Row k="Public token" v={<span className="font-mono text-xs break-all">{detail.public_token}</span>} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <ActBtn label="Regenerate Code" icon="RefreshCw" onClick={() => regen(detail.id)} testid="regen-code" />
                {detail.status !== "DISABLED" ? <ActBtn label="Disable" icon="PauseCircle" onClick={() => act(detail.id, "disable")} testid="disable-tag" /> : <ActBtn label="Activate" icon="Play" onClick={() => act(detail.id, "activate")} testid="activate-tag" />}
                <ActBtn label="Revoke" icon="Ban" danger onClick={() => act(detail.id, "revoke")} testid="revoke-tag" />
                <TransferBtn tagId={detail.id} onDone={() => openDetail(detail.id)} />
              </div>
              {detail.ownership_history?.length > 0 && (
                <div className="mt-3">
                  <p className="font-display font-bold text-sm mb-2">Ownership history</p>
                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    {detail.ownership_history.map((h) => (
                      <div key={h.id} className="flex justify-between"><span>{h.action}</span><span>{new Date(h.created_at).toLocaleString()}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex gap-2"><span className="text-muted-foreground w-24 shrink-0">{k}</span><span className="font-medium">{v}</span></div>;
}
function ActBtn({ label, icon, onClick, danger, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`tap flex items-center justify-center gap-2 py-2.5 rounded-xl border font-semibold text-sm ${danger ? "border-status-lost/40 text-status-lost" : "border-border"}`}><Icon name={icon} className="w-4 h-4" /> {label}</button>;
}
function TransferBtn({ tagId, onDone }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const submit = async () => {
    try { await api.post(`/admin/tags/${tagId}/transfer`, { email }); toast.success("Ownership transferred"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button data-testid="transfer-owner" onClick={() => setOpen(true)} className="tap flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border font-semibold text-sm"><Icon name="ArrowLeftRight" className="w-4 h-4" /> Transfer</button>
      <DialogContent className="rounded-2xl max-w-sm">
        <DialogHeader><DialogTitle>Transfer ownership</DialogTitle></DialogHeader>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="New owner email" className="w-full h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" data-testid="transfer-email" />
        <button onClick={submit} className="tap w-full h-12 rounded-full bg-primary text-primary-foreground font-bold">Transfer</button>
      </DialogContent>
    </Dialog>
  );
}
